/**
 * Incoming Materials Redux Slice
 */

import { createSlice, type PayloadAction } from '@spcc/core'
import type { GoodsReceipt, InboundShipment, InboundShipmentStats, InboundShipmentsPagination, Material, Supplier, PurchaseOrder, StorageLocation, CreateGoodsReceiptForm, CreatePurchaseOrderForm, CreateSupplierForm, CreateMaterialForm } from '../domain/types'
import type { PaginationMeta, PurchaseOrdersListParams } from '../infrastructure/api/incoming-materials-api'
import type { UpdateInboundShipmentRequest } from '../infrastructure/api/inbound-shipments-api'

export interface IncomingMaterialsState {
  goodsReceipts: GoodsReceipt[]
  suppliers: Supplier[]
  materials: Material[]
  purchaseOrders: PurchaseOrder[]
  storageLocations: StorageLocation[]
  poPagination: PaginationMeta
  inboundShipments: InboundShipment[]
  inboundShipmentStats: InboundShipmentStats
  inboundShipmentsPagination: InboundShipmentsPagination
  isLoadingReceipts: boolean
  isLoadingSuppliers: boolean
  isLoadingMaterials: boolean
  isLoadingPurchaseOrders: boolean
  isLoadingStorageLocations: boolean
  isLoadingInboundShipments: boolean
  isUpdatingInboundShipment: boolean
  isSubmitting: boolean
  error: string | null
  lastCreatedGoodsReceiptId: string | null
  lastCreatedPurchaseOrderId: string | null
  lastCreatedMaterialId: string | null
  lastCreatedSupplierId: string | null
  /** id of the purchase order currently being (re)sent to ADS, if any. */
  syncingToAdsPoId: number | null
}

const initialState: IncomingMaterialsState = {
  goodsReceipts: [],
  suppliers: [],
  materials: [],
  purchaseOrders: [],
  storageLocations: [],
  poPagination: { page: 1, pageSize: 25, pageCount: 1, total: 0 },
  inboundShipments: [],
  inboundShipmentStats: {
    arrived: 0,
    first_weight_done: 0,
    unloaded: 0,
    second_weight_done: 0,
    grn_posted: 0,
    grn_skipped: 0,
    total: 0,
  },
  inboundShipmentsPagination: { page: 1, pageSize: 25, pageCount: 1, total: 0 },
  isLoadingReceipts: false,
  isLoadingSuppliers: false,
  isLoadingMaterials: false,
  isLoadingPurchaseOrders: false,
  isLoadingStorageLocations: false,
  isLoadingInboundShipments: false,
  isUpdatingInboundShipment: false,
  isSubmitting: false,
  error: null,
  lastCreatedGoodsReceiptId: null,
  lastCreatedPurchaseOrderId: null,
  lastCreatedMaterialId: null,
  lastCreatedSupplierId: null,
  syncingToAdsPoId: null,
}

const slice = createSlice({
  name: 'incomingMaterials',
  initialState,
  reducers: {
    fetchGoodsReceipts: (state) => {
      state.isLoadingReceipts = true
      state.error = null
    },
    fetchGoodsReceiptsSuccess: (state, action: PayloadAction<GoodsReceipt[]>) => {
      state.isLoadingReceipts = false
      state.goodsReceipts = action.payload
    },
    fetchGoodsReceiptsFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingReceipts = false
      state.error = action.payload
    },

    fetchSuppliers: (state, _action: PayloadAction<{ search?: string; pageSize?: number } | undefined>) => {
      state.isLoadingSuppliers = true
      state.error = null
    },
    fetchSuppliersSuccess: (state, action: PayloadAction<Supplier[]>) => {
      state.isLoadingSuppliers = false
      state.suppliers = action.payload
    },
    fetchSuppliersFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingSuppliers = false
      state.error = action.payload
    },

    fetchMaterials: (state, _action: PayloadAction<{ search?: string; pageSize?: number } | undefined>) => {
      state.isLoadingMaterials = true
      state.error = null
    },
    fetchMaterialsSuccess: (state, action: PayloadAction<Material[]>) => {
      state.isLoadingMaterials = false
      state.materials = action.payload
    },
    fetchMaterialsFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingMaterials = false
      state.error = action.payload
    },

    fetchStorageLocations: (state) => {
      state.isLoadingStorageLocations = true
      state.error = null
    },
    fetchStorageLocationsSuccess: (state, action: PayloadAction<StorageLocation[]>) => {
      state.isLoadingStorageLocations = false
      state.storageLocations = action.payload
    },
    fetchStorageLocationsFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingStorageLocations = false
      state.error = action.payload
    },

    fetchPurchaseOrders: (state, _action: PayloadAction<PurchaseOrdersListParams | undefined>) => {
      state.isLoadingPurchaseOrders = true
      state.error = null
    },
    fetchPurchaseOrdersSuccess: (state, action: PayloadAction<{ data: PurchaseOrder[]; pagination: PaginationMeta }>) => {
      state.isLoadingPurchaseOrders = false
      state.purchaseOrders = action.payload.data
      state.poPagination = action.payload.pagination
    },
    fetchPurchaseOrdersFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingPurchaseOrders = false
      state.error = action.payload
    },

    createGoodsReceipt: (state, _action: PayloadAction<CreateGoodsReceiptForm>) => {
      state.isSubmitting = true
      state.error = null
      state.lastCreatedGoodsReceiptId = null
    },
    createGoodsReceiptSuccess: (state, action: PayloadAction<{ documentId: string }>) => {
      state.isSubmitting = false
      state.lastCreatedGoodsReceiptId = action.payload.documentId
    },
    createGoodsReceiptFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false
      state.error = action.payload
    },

    createPurchaseOrder: (state, _action: PayloadAction<CreatePurchaseOrderForm>) => {
      state.isSubmitting = true
      state.error = null
      state.lastCreatedPurchaseOrderId = null
    },
    createPurchaseOrderSuccess: (state, action: PayloadAction<{ documentId: string }>) => {
      state.isSubmitting = false
      state.lastCreatedPurchaseOrderId = action.payload.documentId
    },
    createPurchaseOrderFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false
      state.error = action.payload
    },

    createSupplier: (state, _action: PayloadAction<CreateSupplierForm>) => {
      state.isSubmitting = true
      state.error = null
      state.lastCreatedSupplierId = null
    },
    createSupplierSuccess: (state, action: PayloadAction<Supplier>) => {
      state.isSubmitting = false
      state.suppliers.unshift(action.payload)
      state.lastCreatedSupplierId = action.payload.documentId
    },
    createSupplierFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false
      state.error = action.payload
    },

    createMaterial: (state, _action: PayloadAction<CreateMaterialForm>) => {
      state.isSubmitting = true
      state.error = null
      state.lastCreatedMaterialId = null
    },
    createMaterialSuccess: (state, action: PayloadAction<Material>) => {
      state.isSubmitting = false
      state.materials.unshift(action.payload)
      state.lastCreatedMaterialId = action.payload.documentId
    },
    createMaterialFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false
      state.error = action.payload
    },

    clearLastCreatedIds: (state) => {
      state.lastCreatedGoodsReceiptId = null
      state.lastCreatedPurchaseOrderId = null
      state.lastCreatedMaterialId = null
      state.lastCreatedSupplierId = null
    },

    fetchInboundShipments: (state, _action: PayloadAction<{ page?: number; pageSize?: number; status?: string | string[]; search?: string; sapSyncStatus?: string; grnStatus?: string; weightStatus?: string; materialId?: string; supplierId?: string } | undefined>) => {
      state.isLoadingInboundShipments = true
      state.error = null
    },
    fetchInboundShipmentsSuccess: (state, action: PayloadAction<{ data: InboundShipment[]; pagination: InboundShipmentsPagination; stats?: InboundShipmentStats }>) => {
      state.isLoadingInboundShipments = false
      state.inboundShipments = action.payload.data
      state.inboundShipmentsPagination = action.payload.pagination
      if (action.payload.stats) {
        state.inboundShipmentStats = action.payload.stats
      }
    },
    fetchInboundShipmentsFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingInboundShipments = false
      state.error = action.payload
    },
    fetchInboundShipmentStats: (_state, _action: PayloadAction<{ search?: string; sapSyncStatus?: string; grnStatus?: string; weightStatus?: string; materialId?: string; supplierId?: string } | undefined>) => {
      // Background refresh only.
    },
    fetchInboundShipmentStatsSuccess: (state, action: PayloadAction<InboundShipmentStats>) => {
      state.inboundShipmentStats = action.payload
    },
    fetchInboundShipmentStatsFailure: () => {
      // Keep the existing stats if this auxiliary request fails.
    },

    updateInboundShipment: (state, _action: PayloadAction<{ documentId: string } & UpdateInboundShipmentRequest>) => {
      state.isUpdatingInboundShipment = true
      state.error = null
    },
    updateInboundShipmentSuccess: (state, action: PayloadAction<InboundShipment>) => {
      state.isUpdatingInboundShipment = false
      const index = state.inboundShipments.findIndex((s) => s.documentId === action.payload.documentId)
      if (index !== -1) {
        state.inboundShipments[index] = action.payload
      }
    },
    updateInboundShipmentFailure: (state, action: PayloadAction<string>) => {
      state.isUpdatingInboundShipment = false
      state.error = action.payload
    },

    resendPurchaseOrderToAds: (state, action: PayloadAction<{ purchaseOrderId: number }>) => {
      state.syncingToAdsPoId = action.payload.purchaseOrderId
    },
    resendPurchaseOrderToAdsSuccess: (state) => {
      state.syncingToAdsPoId = null
    },
    resendPurchaseOrderToAdsFailure: (state) => {
      state.syncingToAdsPoId = null
    },

    clearError: (state) => {
      state.error = null
    },
  },
})

export const incomingMaterialsActions = slice.actions
export const incomingMaterialsReducer = slice.reducer
